import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { Avatar } from '@components/avatar';
import { createForm, useCreateFormStore } from '@components/form';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { MaxLengthCounter } from '@components/max-length-counter';
import { useSidebar } from '@components/sidebar';
import { Sidebar, hideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useCreateJourney, useEditJourney } from 'api/journeys/hooks';
import { JourneyType } from 'api/types';
import { routePaths } from 'ui/authenticated/navigation';
export const useJourneyDetailsSidebar = () =>
  useSidebar(JourneyDetailsSidebar, {
    variant: 'REGULAR',
  });

type JourneyFormData = {
  title: string;
  description: string;
};

const Form = createForm<JourneyFormData>();

type JourneyDetailsSidebarJourney = Pick<
  JourneyType,
  'shortId' | 'title' | 'description' | 'workspace'
> &
  Partial<Pick<JourneyType, 'createdBy' | 'dateCreated'>>;

export const JourneyDetailsSidebar = ({ journey }: { journey: JourneyDetailsSidebarJourney }) => {
  const { t } = useScopedTranslate(locale);
  const updateJourney = useEditJourney({ onSuccess: hideSidebar });
  const create = useCreateJourney({ onSuccess: hideSidebar });
  const navigate = useNavigate();
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();

  const [isCharacterLimitExceeded, setIsCharacterLimitExceeded] = useState(false);

  const handleSubmit = async (formData: JourneyFormData) => {
    if (journey.shortId) {
      await updateJourney.mutateAsync({ journeyId: journey.shortId, ...formData });
    } else {
      const data = await create.mutateAsync({ workspace: journey.workspace, ...formData });
      navigate(
        generatePath(routePaths.editJourney, {
          workspaceId: journey.workspace,
          journeyId: data.shortId,
          '*': '',
        }),
        {
          replace: true,
        }
      );
    }
  };

  const description = (formStore.useFieldValue('description') as string) ?? '';

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t('journeyDetails')}
        icon={IconJourneyItem}
        iconColor="elementBlue100"
      />
      <Sidebar.Content>
        <Form store={formStore} onSubmit={handleSubmit}>
          <Form.TextField
            id="title"
            label={t('title')}
            maxLength={50}
            defaultValue={journey.title}
            autoFocus
          />
          <Stack space={1}>
            <Form.Textarea
              id="description"
              label={t('description')}
              defaultValue={journey.description ?? ''}
            />
            <MaxLengthCounter
              currentLength={description.length}
              maxLength={255}
              onExceedStatusChange={setIsCharacterLimitExceeded}
            />
          </Stack>
          {journey.createdBy && journey.dateCreated && (
            <Stack space={2}>
              <Text text={t('createdBy')} font="semibold14" color="textPrimary" />
              <Stack orientation="horizontal" space={2} align="center">
                <Avatar
                  size="sm"
                  src={journey.createdBy.picture ?? undefined}
                  name={journey.createdBy.name}
                  color="purple"
                />
                <Text
                  font="regular14"
                  color="textSecondary"
                  text={`${journey.createdBy.name} • ${abbreviateDate(
                    journey.dateCreated,
                    'en',
                    'datetime'
                  )}`}
                />
              </Stack>
            </Stack>
          )}
          <Sidebar.Footer>
            <Form.Actions>
              <Form.Reset
                text={t('core.cancel')}
                variant="secondary"
                onClick={hideSidebar}
                isDisabled={updateJourney.isLoading}
              />
              <Form.Submit
                text={t('core.save')}
                variant="primary-blue"
                isDisabled={updateJourney.isLoading || !isDirty || isCharacterLimitExceeded}
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

export const locale = {
  en: {
    journeyDetails: 'Journey details',
    title: 'Title',
    description: 'Description',
    createdBy: 'Created by',
  },
};
